import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { AuthUser } from '../services/auth'
import { fetchCurrentUser, getGoogleLoginUrl, logoutUser } from '../services/auth'

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  hasCompletedOnboarding: boolean
  isLoading: boolean
  refresh: () => Promise<void>
  beginGoogleLogin: (redirectTo?: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await fetchCurrentUser()
      if (response.isAuthenticated && response.user) {
        setUser(response.user)
        setHasCompletedOnboarding(response.hasCompletedOnboarding ?? false)
      } else {
        setUser(null)
        setHasCompletedOnboarding(false)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const beginGoogleLogin = useCallback(async (redirectTo?: string) => {
    const loginUrl = await getGoogleLoginUrl(redirectTo ?? window.location.href)
    window.location.href = loginUrl
  }, [])

  const logout = useCallback(async () => {
    try {
      await logoutUser()
    } finally {
      // Clear cached progression/financial data so the next browser user
      // doesn't see the previous session's state. Keys owned by
      // ProgressionContext (see resetProgression there).
      try {
        localStorage.removeItem('progression_state')
        localStorage.removeItem('form_data')
        localStorage.removeItem('results_summary')
      } catch (error) {
        console.error({
          message: 'Error clearing progression data on logout',
          error: error instanceof Error ? error.message : String(error),
          context: { action: 'logout' },
        })
      }
      await refresh()
    }
  }, [refresh])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    isAuthenticated: Boolean(user),
    hasCompletedOnboarding,
    isLoading,
    refresh,
    beginGoogleLogin,
    logout
  }), [user, hasCompletedOnboarding, isLoading, refresh, beginGoogleLogin, logout])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
